import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { Button, Card, Heading, Select } from "@nn-design-system/react-component-library";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useFetch from "../CustomHooks/useFetch";
import { InsuredPersons, setCurrentlyReportedPerson, setIsSelectedPerson } from "../redux/reducers/authReducer";
import { setPagesToRender } from "../redux/reducers/pagesToRenderReducer";
import { setClientInfo } from "../redux/reducers/claimReducer";
import { setToken } from "../redux/reducers/tokenReducer";
import { StoreType } from "../redux/store/store";
import { claimsType } from "../Config/types/types";

type StelaPersonSelectionFieldProps = {
   insuredPersons: InsuredPersons[];
};

type ConfirmPersonResponse = {
   jwtToken: string;
};

type GetClientClaimsResponse = {
   result: {
      result: claimsType[];
      claimForPersonId: string;
   };
};

const StelaPersonSelectionField: React.FC<StelaPersonSelectionFieldProps> = ({ insuredPersons }) => {
   const { returnUrl, entryJwtToken, proposalNumber, insuredPersonBirthNumber } = useSelector((state: StoreType) => state.authState.stelaContext);
   const [selectedPersonalId, setSelectedPersonalId] = useState<string>(() => {
      const birthNumber = insuredPersonBirthNumber.replace(/[\s/]/g, "");
      if (!birthNumber) {
         return "";
      }

      return insuredPersons.find((person) => person.personalId.replace(/[\s/]/g, "") === birthNumber)?.personalId ?? "";
   });
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const { sendRequest: confirmPerson, data: confirmData } = useFetch<ConfirmPersonResponse>();
   const { sendRequest: getClientClaims, data: claimsData } = useFetch<GetClientClaimsResponse>();

   const continueWithPerson = () => {
      confirmPerson({
         method: "POST",
         url: `${process.env.REACT_APP_BaseURL}/stela2/confirm-person`,
         headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${entryJwtToken}`,
         },
         data: { personalId: selectedPersonalId },
      });
   };

   useEffect(() => {
      if (!confirmData) {
         return;
      }

      dispatch(setToken(confirmData.jwtToken));
      const selectedPerson = insuredPersons.find((person) => person.personalId === selectedPersonalId);
      if (!selectedPerson) {
         return;
      }

      dispatch(setCurrentlyReportedPerson({
         name: `${selectedPerson.firstname} ${selectedPerson.lastname}`,
         birthNumber: selectedPerson.personalId,
      }));
      getClientClaims({
         method: "POST",
         url: process.env.REACT_APP_BaseURL! + process.env.REACT_APP_GetClientClaims,
         headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${confirmData.jwtToken}`,
         },
         data: {
            personalId: selectedPerson.personalId,
            contractId: proposalNumber,
            claimForPersonId: selectedPerson.personalId,
         },
      });
   }, [confirmData, dispatch, insuredPersons, selectedPersonalId]);

   useEffect(() => {
      if (!claimsData) {
         return;
      }

      dispatch(setPagesToRender(claimsData.result.result));
      dispatch(setIsSelectedPerson(true));
      dispatch(setClientInfo({
         personalId: claimsData.result.claimForPersonId,
         claimForPersonId: claimsData.result.claimForPersonId,
         contractId: proposalNumber,
      }));
      navigate("/claimSelection/authorized");
   }, [claimsData, dispatch, navigate]);

   return (
      <Box sx={{ position: "relative", zIndex: "100", width: "100%" }}>
         <Card variant="Attentional">
            <Heading hasMargin={false} variant="Small">
               Pojistnou událost hlásím za
            </Heading>
            <Select
               value={selectedPersonalId}
               onChange={(event) => setSelectedPersonalId(event.target.value as string)}
               options={insuredPersons.map((person) => ({
                  value: person.personalId,
                  text: `${person.firstname} ${person.lastname} - ${person.personalId.slice(0, -4)}/${person.personalId.slice(-4)}`,
               }))}
               placeholderText="Vyberte osobu"
            />
            <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: "1.5rem" }}>
                {selectedPersonalId ? (
                   <Button onClick={continueWithPerson}>
                      Pokračovat
                   </Button>
                ) : null}
               <Button variant="PlainSmall" onClick={() => { window.location.href = returnUrl; }}>
                  Zpět
               </Button>
            </Box>
         </Card>
      </Box>
   );
};

export default StelaPersonSelectionField;
